/**
 * format — shared formatting utilities
 *
 * Consolidates the 3 duplicate renderValue implementations
 * from EntityListPage, HumanView, and BasicView into ONE source of truth.
 *
 * All functions are pure — testable with JSON input/output.
 */

import { createElement, type ReactNode } from 'react';

/** UUID v4 regex — matches standard 8-4-4-4-12 hex format */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Return true if value looks like a UUID/GUID */
export function isGuidValue(val: unknown): boolean {
  return typeof val === 'string' && UUID_RE.test(val);
}

/** Return true if field name is an internal GUID reference (id or *Id/*_id foreign keys) */
export function isGuidField(name: string): boolean {
  return name === 'id' || name === 'tenantId' || /Id$/.test(name) || /_id$/.test(name);
}

/**
 * Get the resolved locale string for date/number formatting.
 * Reads from localStorage to stay in sync with i18n.
 */
export function getLocale(): string {
  const lang = localStorage.getItem('boox_lang') ?? 'en';
  if (lang === 'zh') return 'zh-CN';
  if (lang === 'zh-Hant') return 'zh-TW';
  if (lang === 'ja') return 'ja-JP';
  if (lang === 'ms') return 'ms-MY';
  if (lang === 'id') return 'id-ID';
  if (lang === 'th') return 'th-TH';
  if (lang === 'vi') return 'vi-VN';
  return 'en-US';
}

const STRUCTURED_SUMMARY_KEYS = ['label', 'name', 'title', 'code', 'number', 'id', 'value'] as const;

function clipText(text: string, max = 60): string {
  return text.length > max ? `${text.slice(0, Math.max(0, max - 3))}...` : text;
}

function summarizeObject(value: Record<string, unknown>, depth = 0): string {
  for (const key of STRUCTURED_SUMMARY_KEYS) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) return clipText(candidate.trim());
    if (typeof candidate === 'number' || typeof candidate === 'boolean') return String(candidate);
  }

  const entries = Object.entries(value).filter(([, v]) => v != null);
  if (entries.length === 0) return '{}';

  const parts = entries.slice(0, 3).map(([key, item]) => {
    const rendered = summarizeValue(item, depth + 1, 24);
    return `${key}: ${rendered}`;
  });
  const suffix = entries.length > 3 ? ` +${entries.length - 3}` : '';
  return clipText(parts.join(', ') + suffix);
}

export function summarizeValue(value: unknown, depth = 0, max = 60): string {
  if (value == null) return '—';
  if (typeof value === 'string') return clipText(value, max);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    const rendered = value
      .slice(0, 3)
      .map((item) => summarizeValue(item, depth + 1, 20))
      .join(', ');
    return clipText(`${rendered}${value.length > 3 ? ` +${value.length - 3}` : ''}`, max);
  }

  if (typeof value === 'object') {
    if (depth > 1) return '{...}';
    return clipText(summarizeObject(value as Record<string, unknown>, depth), max);
  }

  return clipText(String(value), max);
}

const STATUS_ACRONYMS = new Set(['ASN', 'EHS', 'GRN', 'PO', 'QC', 'SKU', 'UOM', 'WH', 'WIP', 'WMS']);

export function formatStatusText(value: unknown): string {
  const text = summarizeValue(value);
  if (text === '—') return text;
  return text
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => {
      const upper = word.toUpperCase();
      if (STATUS_ACRONYMS.has(upper)) return upper;
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(' ');
}

/**
 * Format a cell value for display in tables.
 * Returns a string or simple ReactNode description (for JSON-level testing,
 * the pure version `formatValuePure` returns only strings).
 */
/** Parse .NET /Date(ms)/ JSON format to a JS Date, or fall back to new Date(). */
function parseDate(v: string | number | Date): Date {
  if (typeof v === 'string') {
    const m = v.match(/\/Date\((-?\d+)\)\//);
    if (m) return new Date(Number(m[1]));
  }
  return new Date(v instanceof Date ? v : String(v));
}

export function formatCell(value: unknown, format?: string): ReactNode {
  if (value == null) return '—';

  if (format === 'status' || format === 'enum') return formatStatusText(value);

  if (format === 'link' && typeof value === 'string' && value.trim()) {
    return createElement(
      'a',
      {
        href: value,
        target: '_blank',
        rel: 'noreferrer',
        className: 'text-primary underline-offset-2 hover:underline',
        onClick: (event: MouseEvent) => event.stopPropagation(),
      },
      'Open',
    );
  }

  if (format === 'date' && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
    try {
      const d = parseDate(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString(getLocale());
    } catch { /* fall through */ }
    return summarizeValue(value);
  }

  if (format === 'datetime' && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
    try {
      const d = parseDate(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleString(getLocale());
    } catch { /* fall through */ }
    return summarizeValue(value);
  }

  if (format === 'currency' || format === 'money' || format === 'decimal') {
    if (typeof value === 'number' || typeof value === 'string') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return num.toLocaleString(getLocale(), { minimumFractionDigits: 2 });
      }
    }
    return summarizeValue(value);
  }

  if (format === 'number') {
    if (typeof value === 'number' || typeof value === 'string') {
      const num = Number(value);
      if (!Number.isNaN(num)) return num.toLocaleString(getLocale());
    }
    return summarizeValue(value);
  }

  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return summarizeValue(value);
}

/**
 * Pure string-only formatting (for JSON-level testing).
 * Same logic as formatCell but always returns a string — no JSX.
 */
export function formatValuePure(value: unknown, format?: string): string {
  if (value == null) return '—';
  if (format === 'status' || format === 'enum') return formatStatusText(value);
  if (format === 'link' && typeof value === 'string' && value.trim()) return value;
  if (format === 'date' && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
    try { const d = parseDate(value); if (!Number.isNaN(d.getTime())) return d.toLocaleDateString(getLocale()); } catch { /* fall through */ }
    return summarizeValue(value);
  }
  if (format === 'datetime' && (typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
    try { const d = parseDate(value); if (!Number.isNaN(d.getTime())) return d.toLocaleString(getLocale()); } catch { /* fall through */ }
    return summarizeValue(value);
  }
  if (format === 'currency' || format === 'money' || format === 'decimal') {
    if (typeof value === 'number' || typeof value === 'string') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return num.toLocaleString(getLocale(), { minimumFractionDigits: 2 });
      }
    }
    return summarizeValue(value);
  }
  if (format === 'number') {
    if (typeof value === 'number' || typeof value === 'string') {
      const num = Number(value);
      if (!Number.isNaN(num)) return num.toLocaleString(getLocale());
    }
    return summarizeValue(value);
  }
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  return summarizeValue(value);
}

/**
 * Format a relative time string (e.g., "3 min ago", "2 hours ago").
 * Reads i18n translations from the t function passed in, or falls back to English.
 */
export function formatRelativeTime(
  iso: string,
  t?: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);

  const tr = (key: string, opts?: Record<string, unknown>) =>
    t ? t(key, opts) : fallbackRelative(key, opts);

  if (mins < 1) return tr('notification.justNow');
  if (mins < 60) return tr('notification.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return tr('notification.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return tr('notification.daysAgo', { count: days });
}

function fallbackRelative(key: string, opts?: Record<string, unknown>): string {
  const count = opts?.count ?? 0;
  if (key === 'notification.justNow') return 'just now';
  if (key === 'notification.minutesAgo') return `${count} min ago`;
  if (key === 'notification.hoursAgo') return `${count} hr ago`;
  if (key === 'notification.daysAgo') return `${count} days ago`;
  return key;
}
